/**
 * Saved Views and Favorites Types
 *
 * This file defines the complete type structure for saved views and favorites feature.
 * Saved views allow users to save filter configurations for quick access across pages.
 *
 * Database Tables:
 * - public.saved_views: Stores filter configurations with sharing options
 * - public.saved_view_favorites: Tracks which views are starred by users
 *
 * Feature Flags:
 * - NEXT_PUBLIC_FEATURE_SAVED_VIEWS: Enable/disable saved views feature
 * - NEXT_PUBLIC_FEATURE_SAVED_VIEW_FAVORITES: Enable/disable favorites feature
 *
 * @see apps/hub/documents/development/saved-views-and-favorites.md
 */

/**
 * Visibility level for saved views
 * - private: Only visible to the creator
 * - shared: Visible to specific users listed in shared_with_user_ids
 * - public: Visible to all users in the organization
 */
export type SavedViewVisibility = "private" | "shared" | "public";

/**
 * `public.saved_views.visibility` is a plain `text` column with no CHECK
 * constraint, so a row read back from the database is only a `string` as far as
 * the generated types are concerned. Narrow it here instead of asserting.
 *
 * Every writer in the app is typed to {@link SavedViewVisibility}, so an
 * unrecognised value means hand-edited or corrupted data — fall back to the
 * least-permissive setting rather than showing a view as shared/public.
 */
export function toSavedViewVisibility(value: string): SavedViewVisibility {
  switch (value) {
    case "private":
    case "shared":
    case "public":
      return value;
    default:
      return "private";
  }
}

/**
 * Resource type that can have saved views — the entity name of the page the
 * view applies to ("contacts", "tickets", "time_entries", "companies", "deals",
 * "tasks", …).
 *
 * This is deliberately `string`, not a closed union: `public.saved_views.resource`
 * is a plain `text` column with no CHECK constraint, and every
 * `UniversalEntityPage` passes its own `entityName` straight through, so any
 * registered entity can own saved views. A narrower union here was already
 * being defeated by `resource as SavedViewResource` at the call sites.
 */
export type SavedViewResource = string;

/**
 * Base saved view record from Supabase
 * This is the complete database record structure
 *
 * @example
 * {
 *   id: "ef990cb8-a1a6-4f5a-b3ee-dcc0a6bec586",
 *   name: "prospects-customers",
 *   resource: "contacts",
 *   filters: {
 *     globalSearch: "",
 *     filter_status: ["prospect", "customer"],
 *     filter_company_name: "",
 *     filter_contact_type: null
 *   },
 *   visibility: "private",
 *   shared_with_user_ids: [],
 *   created_by: "ff4bf243-c6e0-4cc3-b4e1-238b2335b35a",
 *   created_at: "2025-09-27T04:04:09.029222+00:00",
 *   updated_at: "2025-09-27T04:04:09.029222+00:00"
 * }
 */
export interface SavedView {
  /** UUID primary key */
  id: string;

  /** User-friendly name for the view */
  name: string;

  /** Resource type this view applies to (contacts, tickets, etc.) */
  resource: SavedViewResource;

  /**
   * Filter configuration as JSON
   * Structure is resource-specific and defined by each page's filter config
   *
   * @example Contacts filters
   * {
   *   globalSearch: "",
   *   filter_status: ["prospect", "customer"],
   *   filter_company_name: "",
   *   filter_contact_type: null,
   *   filter_tags: ["vip"]
   * }
   *
   * @example Tickets filters
   * {
   *   status: ["open"],
   *   assigneeIds: ["..."],
   *   query: ""
   * }
   *
   * @example Time Entries filters
   * {
   *   dateRange: { from: "2025-09-01", to: "2025-09-30" },
   *   clientIds: ["..."]
   * }
   */
  /**
   * NOTE: this is really a {@link SerializedViewState} — `useSavedViews` parses
   * the jsonb column into one. It stays widened here because
   * `UniversalEntityPage`'s `SavedViewsStateTypes.allViews` is declared
   * `Array<SavedViewConfig | SavedView>`, and `SavedViewConfig.filters` is a
   * FLAT `TableFilters | AdvancedFilters`, not the envelope. Narrowing this
   * field surfaces that mismatch as a type error in SavedViewsTabsContainer.
   * Fix the `allViews` union (it only ever holds `SavedViewConfig`), then
   * narrow this to `SerializedViewState`.
   */
  filters: Record<string, any>;

  /** Visibility level: private (owner only), shared (specific users), or public (all users) */
  visibility: SavedViewVisibility;

  /** Array of user IDs who can see this view (only used when visibility='shared') */
  shared_with_user_ids: string[];

  /** UUID of the user who created this view */
  created_by: string;

  /** ISO 8601 timestamp when view was created */
  created_at: string;

  /** ISO 8601 timestamp when view was last updated */
  updated_at: string;
}

/**
 * Saved view favorite record
 * Tracks which views are starred by which users
 *
 * @example
 * {
 *   user_id: "ff4bf243-c6e0-4cc3-b4e1-238b2335b35a",
 *   view_id: "ef990cb8-a1a6-4f5a-b3ee-dcc0a6bec586",
 *   created_at: "2025-09-27T04:04:09.029222+00:00"
 * }
 */
export interface SavedViewFavorite {
  /** UUID of the user who favorited this view */
  user_id: string;

  /** UUID of the saved view being favorited */
  view_id: string;

  /** ISO 8601 timestamp when favorite was created */
  created_at: string;
}

/**
 * Input type for creating a new saved view
 * Omits auto-generated fields (id, created_at, updated_at)
 */
export interface CreateSavedViewInput {
  name: string;
  resource: SavedViewResource;
  filters: Record<string, any>;
  visibility?: SavedViewVisibility; // Defaults to 'private'
  shared_with_user_ids?: string[]; // Defaults to []
}

/**
 * Input type for updating an existing saved view
 * All fields are optional
 */
export interface UpdateSavedViewInput {
  name?: string;
  filters?: Record<string, any>;
  visibility?: SavedViewVisibility;
  shared_with_user_ids?: string[];
}

/**
 * Grouped saved views for UI display
 * Views are grouped by visibility and ownership
 */
export interface GroupedSavedViews {
  /** Views created by the current user */
  myViews: SavedView[];

  /** Views shared with the current user by others */
  sharedWithMe: SavedView[];

  /** Public views visible to all users */
  public: SavedView[];
}

/**
 * Saved view with favorite status
 * Used when displaying views with star indicators
 */
export interface SavedViewWithFavorite extends SavedView {
  /** Whether this view is favorited by the current user */
  isFavorited: boolean;
}

/**
 * Sample saved views for different resources
 * These are real examples from the database to document expected filter structures
 *
 * @see SAVED_VIEW_SAMPLES for actual examples
 */
export const SAVED_VIEW_SAMPLES = {
  /**
   * Contacts saved view example
   * Filters for prospects and customers
   */
  contacts: {
    id: "ef990cb8-a1a6-4f5a-b3ee-dcc0a6bec586",
    name: "prospects-customers",
    resource: "contacts" as const,
    filters: {
      globalSearch: "",
      filter_status: ["prospect", "customer"],
      filter_company_name: "",
      filter_contact_type: null,
    },
    visibility: "private" as const,
    shared_with_user_ids: [],
    created_by: "ff4bf243-c6e0-4cc3-b4e1-238b2335b35a",
    created_at: "2025-09-27T04:04:09.029222+00:00",
    updated_at: "2025-09-27T04:04:09.029222+00:00",
  } as SavedView,

  /**
   * Contacts saved view with tags filter
   */
  contactsWithTags: {
    id: "369efb1a-5d10-4aed-831d-e94e03038a50",
    name: "super",
    resource: "contacts" as const,
    filters: {
      filter_tags: ["super"],
      globalSearch: "",
      filter_company_name: "",
      filter_contact_type: null,
    },
    visibility: "private" as const,
    shared_with_user_ids: [],
    created_by: "ff4bf243-c6e0-4cc3-b4e1-238b2335b35a",
    created_at: "2025-09-27T04:15:55.385808+00:00",
    updated_at: "2025-09-27T04:15:55.385808+00:00",
  } as SavedView,

  /**
   * Contacts saved view with company filter
   */
  contactsByCompany: {
    id: "e1696d9b-7bd8-4d5b-81a7-4031df69364c",
    name: "pwys",
    resource: "contacts" as const,
    filters: {
      globalSearch: "",
      filter_company_name: "play where you stay",
    },
    visibility: "private" as const,
    shared_with_user_ids: [],
    created_by: "ff4bf243-c6e0-4cc3-b4e1-238b2335b35a",
    created_at: "2025-09-27T03:59:22.022909+00:00",
    updated_at: "2025-09-27T03:59:22.022909+00:00",
  } as SavedView,

  /**
   * Empty filters example
   * Shows a view with no active filters
   */
  empty: {
    id: "34d7983f-1fad-43b1-a339-d6b4299213c6",
    name: "asdf",
    resource: "contacts" as const,
    filters: {},
    visibility: "private" as const,
    shared_with_user_ids: [],
    created_by: "ff4bf243-c6e0-4cc3-b4e1-238b2335b35a",
    created_at: "2026-01-25T03:56:32.976994+00:00",
    updated_at: "2026-01-25T03:56:32.976994+00:00",
  } as SavedView,
} as const;

/** Values `visibility` may hold on a row that really is a {@link SavedView}. */
const SAVED_VIEW_VISIBILITIES: readonly string[] = ["private", "shared", "public"];

/**
 * Type guard to check if an object is a SavedView
 *
 * A saved view is UNTRUSTED input: `public.saved_views` is jsonb + plain `text`
 * with no CHECK constraints, and rows outlive the code that wrote them. So this
 * validates the shape it claims rather than trusting `typeof x === "object"`:
 *
 * - `filters` must be a plain object. `typeof null === "object"`, so the old
 *   check waved through `filters: null` (and arrays) and every consumer that
 *   iterates the filter map then throws on a view the guard called valid.
 * - `visibility` must be one of the three known values — the guard asserts the
 *   narrow {@link SavedViewVisibility} union, so accepting any string handed
 *   callers a type that lies. Use {@link toSavedViewVisibility} to COERCE an
 *   unrecognised value (fails closed to "private"); this guard only reports.
 * - `shared_with_user_ids` must be an array of strings, not just an array —
 *   a null entry becomes a "shared with nobody-shaped user" downstream.
 */
export function isSavedView(obj: unknown): obj is SavedView {
  if (typeof obj !== "object" || obj === null) return false;
  const view = obj as Record<string, unknown>;
  return (
    typeof view.id === "string" &&
    typeof view.name === "string" &&
    typeof view.resource === "string" &&
    typeof view.filters === "object" &&
    view.filters !== null &&
    !Array.isArray(view.filters) &&
    typeof view.visibility === "string" &&
    SAVED_VIEW_VISIBILITIES.includes(view.visibility) &&
    Array.isArray(view.shared_with_user_ids) &&
    view.shared_with_user_ids.every((id): id is string => typeof id === "string") &&
    typeof view.created_by === "string" &&
    typeof view.created_at === "string" &&
    typeof view.updated_at === "string"
  );
}

/**
 * Type guard to check if an object is a SavedViewFavorite
 */
export function isSavedViewFavorite(obj: unknown): obj is SavedViewFavorite {
  if (typeof obj !== "object" || obj === null) return false;
  const fav = obj as Record<string, unknown>;
  return (
    typeof fav.user_id === "string" &&
    typeof fav.view_id === "string" &&
    typeof fav.created_at === "string"
  );
}
